/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './Image';

const MIN_WIDTH = 10;
const MAX_WIDTH = 1200;

const MIN_HEIGHT = 10;
const MAX_HEIGHT = 800;

const ImageManager: ComponentManagerFn<'Image'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Image'>(platform, 'Image'),
    type: 'Image',
    name: 'Image',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      image: {
        label: 'Image',
        type: 'ImgAttributeChooser',
        required: true,
        helpText:
          'Choose an image from the dropdown.For any custom images, Upload them on the Image panel and then select them here.',
      },
      mediaHeight: {
        label: 'Height',
        type: 'number',
        max: MAX_HEIGHT,
        min: MIN_HEIGHT,
        category: 'Layout',
      },
      mediaWidth: {
        label: 'Width',
        type: 'number',
        max: MAX_WIDTH,
        min: MIN_WIDTH,
        category: 'Layout',
      },
    },
    getDisplayText: () => 'Media',
    category: 'Forms',
    icon: { iconName: 'image' },
    description: `**Overview**<br/>
The Image component is a simple but essential tool for displaying static graphics within your application. It allows you to select and render visual assets that have been uploaded to the system's Image Panel, such as logos, banners, or instructional diagrams.
Use this component when you need to embed fixed visuals that do not change based on user data (for dynamic, data-driven images, use the **Media** or **Attachment** components instead).

**1. Source Configuration**<br/>
The component renders images directly from the application's internal asset library.
* **Image:** A dropdown selector (ImgAttributeChooser) that lists all available images uploaded to the system.
* **Workflow:** To use a custom image, you must first upload it via the "Image Panel" in the designer. Once uploaded, it will appear in this dropdown for selection.

**2. Dimensions & Layout**<br/>
You have precise control over the display size of the image to ensure it fits your page layout.
* **Height (mediaHeight):** Sets the vertical size of the image in pixels. (Constraints: Minimum 10px, Maximum 800px).
* **Width (mediaWidth):** Sets the horizontal size of the image in pixels. (Constraints: Minimum 10px, Maximum 1200px).

**3. Interactivity**<br/>
The Image component can function as more than just decoration; it can also serve as a navigational or action element.
* **TriggerOnClick:** You can attach a **TriggerOnClick** child component.
* **Function:** This makes the image clickable.
* **Use Case:** Clicking a company logo to return to the dashboard, or clicking a "Help" icon image to open a support modal.

**4. Best Use Cases**<br/>
* **Branding:** Placing a company logo in the header or login screen.
* **Instructional Content:** Displaying a static diagram or flowchart to guide users through a process.
* **Navigation Buttons:** Creating graphical buttons (e.g., large distinctive icons) for a "Home" dashboard menu.
* **Banners:** Adding decorative header images to report pages or form sections.`,
  };
};

export default ImageManager;
